<template lang="pug">
section#episode-page.section.page-section.top-section
  .episode-loading(v-if="settings.loading")
  div(v-else)
    .episode-header
      .container(v-if="!canView")
        auth-form
      .container(v-else)
        video-player(
          v-if="episode._embedded.video.id"
          :video="episode._embedded.video"
        )
        audio-player(
          v-else-if="episode._embedded.audio.id"
          :sources="[episode._embedded.audio.stream_url]",
          :html5="true"
          :preload="true"
          :artwork="episode._embedded.artwork"
        )

    .authenticated(v-if="member.is_approved")
      .container
        span Viewing as <b>{{ member.name }}</b>
        a(@click="signOut") Sign Out

    .episode-body.container
      .columns
        .column.is-two-thirds
          .content.mobile-margin
            b-icon.premium-icon(:icon="member.is_approved ? 'lock-open' : 'lock'" v-if="episode.is_premium")
            h2.episode-title {{ episode.title }}
            h4.collection-title
              router-link(
                v-if="collection.id"
                :to="{ name: 'collection', params: { id: collection.id } }"
              ) {{ collection.title }}
            .episode-meta
              .speaker(v-if="speaker")
                cl-img(
                  v-if="speaker._embedded && speaker._embedded.profile_image"
                  :cl-id="speaker._embedded.profile_image.cl_id"
                  :transform="{ width: 120, height: 120, crop: 'thumb' }"
                )
                div
                  h4 {{ speaker.full_name }}
                  .has-text-grey {{ episode.pub_date | date('dddd MMMM D, YYYY') }}
              .download.is-hidden-touch(v-if="episode.audio_id && canView")
                a.button.is-light(:href="episode._embedded.audio.download_link")
                  b-icon(icon="download")
                  span Download MP3
            read-more.episode-description(:value="episode.long_desc")

          .episode-related
            .columns.is-multiline
              .column.is-half(v-for="related in episode._embedded.related")
                episode-list-item(
                  :hoverable="true"
                  :value="related"
                  type="thumbnail"
                )
        .column.is-one-third
          about-account.mobile-margin(
            v-sticky="!isMobile"
            sticky-offset="{ top: 96 }"
          )
</template>

<script>
import { mapGetters } from 'vuex';
import AboutAccount from '@/components/accounts/AboutAccount.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';
import AuthForm from '@/components/AuthForm.vue';
import EpisodeListItem from '@/components/episodes/EpisodeListItem.vue';
import EpisodeTypeahead from '@/components/episodes/EpisodeTypeahead.vue';
import ReadMore from '@/components/ReadMore.vue';
import Spinner from '@/components/Spinner.vue';
import VideoPlayer from '@/components/VideoPlayer.vue';
import * as actionTypes from '@/store/types/actions';
import * as getterTypes from '@/store/types/getters';
import * as mutationTypes from '@/store/types/mutations';

export default {
  name: 'episode-page',
  components: {
    AboutAccount,
    AudioPlayer,
    AuthForm,
    EpisodeListItem,
    EpisodeTypeahead,
    ReadMore,
    Spinner,
    VideoPlayer,
  },
  computed: {
    ...mapGetters({
      account: getterTypes.GET_ACCOUNT,
      channels: getterTypes.GET_CHANNELS,
      channel: getterTypes.GET_CHANNEL,
      episode: getterTypes.GET_EPISODE,
      member: getterTypes.GET_MEMBER,
    }),
    canView() {
      if (!this.episode.is_premium) return true;
      return this.member.is_approved;
    },
    collection() {
      if (this.episode._embedded.collections.length > 0) {
        return this.episode._embedded.collections[0];
      }
      return {};
    },
    episodeType() {
      return this.channel.title === 'Television' ? 'Episodes' : 'Services';
    },
    isMobile() {
      return window.innerWidth <= 769;
    },
    listState() {
      if (this.channel.title === 'Television') {
        return {
          name: 'tv',
        };
      }
      return {
        name: 'services',
      };
    },
    offline() {
      return this.episode.video_id === null && this.episode.audio_id === null;
    },
    speaker() {
      return this.episode._embedded.speaker;
    },
    videoService() {
      if (this.episode._embedded.video) {
        return this.episode._embedded.video.service;
      }
      return 'youtube';
    },
  },
  created() {
    this.getPage();
  },
  data() {
    return {
      settings: {
        loading: false,
      },
    };
  },
  methods: {
    async getPage() {
      this.settings.loading = true;
      if (this.member.id === undefined) {
        const member = localStorage.getItem(this.$store.getters[getterTypes.GET_MEMBER_LOCAL_STORAGE_KEY]);
        if (member) {
          try {
            await this.$store.dispatch(actionTypes.DO_GET_MEMBER, {
              id: JSON.parse(member).id,
            });
          } catch (e) {
            this.$store.commit(mutationTypes.UNSET_MEMBER);
          }
        } else {
          this.$store.commit(mutationTypes.UNSET_MEMBER);
        }
      }
      const payload = {
        id: this.$route.params.id,
        zoom: 'audio,artwork,channels,collections,speaker,tags,related,video',
        related_count: 4,
      };
      const { data } = await this.$store.dispatch(actionTypes.DO_GET_EPISODE, payload);
      const episode = data.episodes[0];
      if (episode._embedded.channels) {
        const channel = episode._embedded.channels[0];
        this.$store.commit(mutationTypes.SET_CHANNEL, channel);
      }
      this.settings.loading = false;
    },
    signOut() {
      this.$store.commit(mutationTypes.UNSET_MEMBER);
      window.scrollTo(0, 0);
    },
  },
  watch: {
    $route: 'getPage',
  },
};
</script>

<style lang="scss">
@mixin video($height) {
  #episode-page {
    .episode-loading {
      min-height: $height;
    }
    .episode-header {
      display: flex;
      align-items: center;
      .container {
        min-height: $height;
        .video-wrapper {
          height: $height;
        }
      }
    }
  }
}
@media (min-width: $tablet) {
  @include video(calc(95vh - 72px));
}
@media (max-width: $tablet) {
  @include video(calc(100vw * 0.5625));
}
#episode-page {
  padding-bottom: 6rem;
  .authenticated {
    background: $blue;
    color: $white;
    padding: 0.75rem;
    text-align: center;
    a {
      padding-left: 0.5rem;
      &:hover {
        color: $primary;
      }
    }
  }
  .episode-body {
    padding: 3rem 0;
    .premium-icon {
      float: right;
    }
    h4 {
      font-weight: 500 !important;
    }
    .episode-title, .collection-title {
      margin-top: 0;
    }
    .collection-title {
      a {
        color: $grey-darker;
      }
    }
    .content {
      .episode-meta {
        padding: 1.5rem 0;
        display: flex;
        justify-content: space-between;
        .speaker {
          display: flex;
          align-items: center;
          img {
            border-radius: 100px;
            width: 60px;
            height: 60px;
            margin-right: 0.75rem;
          }
        }
        .download {
          display: flex;
          align-items: center;
        }
        h4 {
          margin-bottom: 0.2rem;
        }
      }
      .episode-description {
        line-height: 150%;
      }
    }
  }
  @media (max-width: $tablet) {
    .episode-body {
      padding-top: 1.5rem;
      padding-bottom: 1.75rem;
      // .columns {
      //   .column {
      //     .content {
      //       margin: 0 1.25rem;
      //     }
      //   }
      // }
    }
    .episode-related {
      .columns {
        @include carousel;
      }
    }
  }
}
</style>

<style theme="ffc" lang="scss">
#episode-page {
  background-color: $white-ter !important;
  .episode-header, .episode-loading {
    background-color: #000;
  }
  .episode-body {
    .content {
      background-color: $white;
      padding: 1.5rem;
      border: 1px solid #eaeaea;
    }
  }
}
</style>

<style theme="cwo" lang="scss">
#episode-page {
  .episode-header, .episode-loading {
    background-color: $dark;
    .container {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .episode-body {
    .content {
      background-color: $white;
      padding: 1.5rem;
    }
  }
}
</style>
